import { Avatar, Box, Chip, Stack, Typography } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid-premium';
import MultiFileUploader from 'components/ui/file-uploader/MultiFileUploader';
import { equipmentActivityService } from 'features/inventory/equipments/api/equipment-activity.service';
import { t } from 'i18next';

const ACTIVITY_TYPE_STYLES: Record<string, { label: string; sx: any }> = {
  criticalities: {
    label: 'Criticità',
    sx: {
      color: '#fff',
      bgcolor: '#d32f2f', // Rosso Fuoco (Pieno): salta subito all'occhio per le emergenze
      borderColor: '#b71c1c',
      boxShadow: '0px 2px 4px rgba(211, 47, 47, 0.2)'
    }
  },
  instrument_issues: {
    label: 'Problematiche Strumenti',
    sx: {
      color: '#c43e00', // Arancione Scuro/Ruggine per massima leggibilità del testo
      bgcolor: 'rgba(255, 152, 0, 0.15)', // Sfondo Arancione Soft ben visibile
      borderColor: '#ff9800'
    }
  },
  qc_quality: {
    label: 'QC / Qualità - Calibrazioni',
    sx: {
      color: '#0d47a1', // Blu Elettrico Intenso
      bgcolor: 'rgba(33, 150, 243, 0.15)', // Sfondo Celeste Chiaro
      borderColor: '#2196f3'
    }
  },
  reagents: {
    label: 'Reagenti',
    sx: {
      color: '#4a148c', // Viola Profondo / Inchiostro
      bgcolor: 'rgba(156, 39, 176, 0.12)', // Sfondo Lilla
      borderColor: '#9c27b0'
    }
  },
  samples: {
    label: 'Campioni',
    sx: {
      color: '#1b5e20', // Verd// ← Aggiungi questa rigae Foresta Scuro
      bgcolor: 'rgba(76, 175, 80, 0.15)', // Sfondo Verde Mela Chiaro
      borderColor: '#4caf50'
    }
  },
  baskets: {
    label: 'Cestelli',
    sx: {
      color: '#006064', // Ottanio / Verde Petrolio Scuro
      bgcolor: 'rgba(0, 188, 212, 0.15)', // Sfondo Acqua Marina
      borderColor: '#00bcd4'
    }
  },
  sector_notes: {
    label: 'Note per settore',
    sx: {
      color: '#37474f', // Grigio Lavagna Scuro
      bgcolor: 'rgba(144, 164, 174, 0.18)', // Sfondo Grigio Chiaro Pulito
      borderColor: '#78909c'
    }
  },
  other: {
    label: 'Altro',
    sx: {
      color: 'text.secondary',
      bgcolor: 'action.hover',
      borderColor: 'divider',
      fontStyle: 'italic'
    }
  }
};

const formatDateTime = (dateString?: string) => {
  if (!dateString) return { date: '', time: '' };

  const dateObj = new Date(dateString);
  if (isNaN(dateObj.getTime())) return { date: '', time: '' };

  return {
    date: dateObj.toLocaleDateString('it-IT'),
    time: dateObj.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })
  };
};

export const ActivitiesColumns = (refreshRef: any): GridColDef[] => [
  {
    field: 'sequence_number',
    headerName: 'ID',
    flex: 1,
    minWidth: 65,
    renderCell: (params) => (
      <Typography variant="body2" sx={{ fontSize: '0.85rem', color: '#1e293b', fontWeight: 500 }}>
        {String(params.row.sequence_number ?? '—').padStart(2, '0')}
      </Typography>
    )
  },
  {
    field: 'activity_date',
    headerName: 'Data Creazione',
    minWidth: 125,
    flex: 1,
    type: 'date',
    valueGetter: (params) => {
      const rawValue = params?.value;
      if (!rawValue) return null;
      return new Date(rawValue);
    },
    renderCell: (params) => {
      const { date, time } = formatDateTime(params.value);

      return (
        <Stack justifyContent="center" alignItems="flex-start" sx={{ height: '100%', py: 0.5 }}>
          <Typography
            variant="body2"
            sx={{ fontSize: '0.85rem', fontWeight: 500, lineHeight: 1.2 }}>
            {date}
          </Typography>
          <Typography
            variant="caption"
            sx={{ fontSize: '0.75rem', color: '#64748b', fontWeight: 500 }}>
            {time || '10:30'}
          </Typography>
        </Stack>
      );
    }
  },
  {
    field: 'activity_type_display',
    headerName: 'Tipologia e descrizione attività',
    minWidth: 250,
    flex: 1,
    renderCell: (params) => {
      const activity = params.row;
      const typeLabel = activity.activity_type_display || 'Errore display';
      const typeConfig = ACTIVITY_TYPE_STYLES[activity.activity_type] || ACTIVITY_TYPE_STYLES.other;

      return (
        <Stack spacing={0.25} justifyContent="center" sx={{ height: '100%', py: 0.5, minWidth: 0 }}>
          <Chip
            label={String(typeLabel)}
            size="small"
            sx={{
              fontWeight: 700,
              borderRadius: 1,
              textTransform: 'uppercase',
              fontSize: '0.6rem',
              letterSpacing: '0.5px',
              border: '1px solid',
              height: '18px',
              alignSelf: 'start',
              '& .MuiChip-label': { color: 'inherit', px: 0.75 },
              ...typeConfig.sx
            }}
          />
          <Typography
            variant="caption"
            color="text.secondary"
            noWrap
            sx={{ maxWidth: 260, fontSize: '0.7rem', lineHeight: 1.2 }}>
            {activity.description ?? '—'}
          </Typography>
        </Stack>
      );
    }
  },
  {
    field: 'validation_date',
    headerName: 'Validazione',
    minWidth: 120,
    flex: 1,
    renderCell: (params) => {
      const { date, time } = formatDateTime(params.value);
      return (
        <Stack justifyContent="center" sx={{ height: '100%', py: 0.5 }}>
          {date ? (
            <>
              <Typography
                variant="body2"
                sx={{ fontSize: '0.8rem', fontWeight: 500, lineHeight: 1.2 }}>
                {date}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontSize: '0.7rem', lineHeight: 1.1 }}>
                {time}
              </Typography>
            </>
          ) : (
            <Typography
              variant="caption"
              color="warning.main"
              sx={{ fontSize: '0.7rem', fontStyle: 'italic', lineHeight: 1.1 }}>
              In attesa
            </Typography>
          )}
        </Stack>
      );
    }
  },
  {
    field: 'notes',
    headerName: 'Note',
    minWidth: 240,
    flex: 1,
    renderCell: (params) => (
      <Typography
        variant="body2"
        sx={{
          fontSize: '0.85rem',
          color: '#334155',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap'
        }}>
        {params.value}
      </Typography>
    )
  },
  {
    field: 'attachments',
    headerName: 'Allegati',
    minWidth: 90,
    flex: 1,
    sortable: false,
    filterable: false,
    aggregable: false,
    groupable: false,
    renderCell: (params) => (
      <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
        <MultiFileUploader
          service={equipmentActivityService}
          activityId={params.row.id}
          attachments={params.value || []}
          disableEdit={false}
          onRefresh={() => refreshRef.current?.()}
        />
      </Box>
    )
  },
  {
    field: 'operator_name',
    headerName: 'Operatore',
    flex: 1,
    minWidth: 180,
    renderCell: (params) => {
      const operatorName = params.row.operator_name || '—';
      const operatorRole = params.row.operator_role || 'UNKNOWN';

      const initials =
        operatorName && operatorName !== '—'
          ? operatorName
              .split(' ')
              .filter(Boolean)
              .map((n) => n[0])
              .join('')
              .slice(0, 2)
              .toUpperCase()
          : '';

      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, height: '100%' }}>
          <Avatar
            sx={{
              width: 28,
              height: 28,
              fontSize: '0.75rem',
              fontWeight: 600,
              bgcolor: '#e6f7ff',
              color: '#1890ff',
              border: '1px solid #bae7ff'
            }}>
            {initials}
          </Avatar>

          {/* ANAGRAFICA */}
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 500,
                color: '#262626',
                fontSize: '0.85rem',
                lineHeight: 1.2,
                textTransform: 'capitalize'
              }}>
              {operatorName !== '—' ? operatorName.toLowerCase() : operatorName}
            </Typography>

            <Typography variant="caption" sx={{ color: '#8c8c8c', fontSize: '0.75rem' }}>
              {t(`roles.${operatorRole}`) as string}
            </Typography>
          </Box>
        </Box>
      );
    }
  }
];
